import React, { useState, useEffect } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { ACCENT_COLORS } from '../../config/dashboardTemplates';
import { useAdhanPlayer } from '../../hooks/useAdhanPlayer';

// Import reusable components for data logic
import SimpleTimeCard from '../simpledashboard/SimpleTimeCard';
import SimpleDateCard from '../simpledashboard/SimpleDateCard';
import SimplePrayerCountdownCard from '../simpledashboard/SimplePrayerCountdownCard';
import SimplePrayerTimesCard from '../simpledashboard/SimplePrayerTimesCard';
import SimpleDailyDuaCard from '../simpledashboard/SimpleDailyDuaCard';

const ClassicElegantTemplate = ({ 
  prayerTimes,
  dateTime,
  duas,
  currentDuaIndex,
  onDateChange,
  onDuaChange,
}) => {
  const { settings } = useSettings();
  const accentColor = ACCENT_COLORS[settings.simpleDashboard?.accentColor || 'emerald'];
  const [currentTime, setCurrentTime] = useState(new Date());

  useAdhanPlayer(prayerTimes, settings);

  // Update time for analog clock
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const accentColorValue = accentColor?.base ? `var(--color-${settings.simpleDashboard?.accentColor || 'emerald'}-500)` : '#c9a96e';

  const ClockFace = () => {
    const hours = currentTime.getHours() % 12;
    const minutes = currentTime.getMinutes();
    const seconds = currentTime.getSeconds();

    const hourAngle = (hours * 30) + (minutes * 0.5);
    const minuteAngle = (minutes * 6) + (seconds * 0.1);
    const secondAngle = seconds * 6;

    // Generate hour markers
    const hourMarkers = [];
    for (let i = 0; i < 12; i++) {
      const angle = i * 30;
      const isMajor = i % 3 === 0;
      hourMarkers.push(
        <div 
          key={`hour-${i}`}
          className={`absolute w-1 h-6 bg-amber-600 top-4 left-1/2 origin-bottom rounded-sm ${isMajor ? 'w-1 h-8 bg-gray-800 top-2' : ''}`}
          style={{ 
            transform: `translateX(-50%) rotate(${angle}deg)`,
            transformOrigin: '50% 165px'
          }}
        />
      );
    }

    // Generate minute markers
    const minuteMarkers = [];
    for (let i = 0; i < 60; i++) {
      if (i % 5 !== 0) { // Skip hour positions
        const angle = i * 6;
        minuteMarkers.push(
          <div 
            key={`minute-${i}`}
            className="absolute w-px h-3 bg-amber-600 top-5 left-1/2 origin-bottom"
            style={{ 
              transform: `translateX(-50%) rotate(${angle}deg)`,
              transformOrigin: '50% 160px'
            }}
          />
        );
      }
    }

    return (
      <div className="relative w-96 h-96 rounded-full border-8 border-amber-600 shadow-2xl bg-gradient-to-br from-white via-gray-50 to-gray-100" 
           style={{ borderColor: accentColorValue }}>
        {/* Clock face background */}
        <div className="w-full h-full rounded-full bg-gradient-radial from-white via-gray-50 to-gray-100 relative">
          
          {/* Roman numerals */}
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 text-2xl font-semibold text-gray-800 font-serif">XII</div>
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-2xl font-semibold text-gray-800 font-serif">III</div>
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 text-2xl font-semibold text-gray-800 font-serif">VI</div>
          <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-2xl font-semibold text-gray-800 font-serif">IX</div>
          
          {/* Hour markers */}
          {hourMarkers}
          
          {/* Minute markers */}
          {minuteMarkers}
          
          {/* Clock hands */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            {/* Hour hand */}
            <div 
              className="absolute w-1.5 h-24 bg-gradient-to-t from-gray-800 to-gray-600 rounded-full shadow-lg"
              style={{ 
                transform: `rotate(${hourAngle}deg)`,
                transformOrigin: 'center bottom',
                top: '-96px',
                left: '-3px'
              }}
            />
            {/* Minute hand */}
            <div 
              className="absolute w-1 h-32 bg-gradient-to-t from-gray-800 to-gray-600 rounded-full shadow-lg"
              style={{ 
                transform: `rotate(${minuteAngle}deg)`,
                transformOrigin: 'center bottom',
                top: '-128px',
                left: '-2px'
              }}
            />
            {/* Second hand */}
            <div 
              className="absolute w-0.5 h-36 bg-gradient-to-t from-gray-600 to-amber-500 rounded-full shadow-lg"
              style={{ 
                transform: `rotate(${secondAngle}deg)`,
                transformOrigin: 'center bottom',
                top: '-144px',
                left: '-1px',
                background: `linear-gradient(to top, ${accentColorValue}, #d4af37)`
              }}
            />
            {/* Center dot */}
            <div className="w-4 h-4 bg-gray-800 rounded-full border-2 shadow-lg"
                 style={{ borderColor: accentColorValue }} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-amber-50 to-gray-100 flex items-center justify-center p-10">
      <div className="grid grid-cols-2 gap-16 max-w-6xl w-full bg-white rounded-2xl shadow-2xl p-16 relative overflow-hidden">
        {/* Top border accent */}
        <div 
          className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent"
          style={{ background: `linear-gradient(90deg, transparent, ${accentColorValue}, transparent)` }}
        />
        
        {/* Left Section - Clock and Dua */}
        <div className="flex flex-col items-center justify-center space-y-10">
          {/* Clock Container */}
          <div className="flex flex-col items-center space-y-6">
            <ClockFace />
            
            {/* Digital time display */}
            <SimpleTimeCard 
              render={({ time }) => (
                <div className="text-center">
                  <div className="text-2xl font-medium text-gray-800 font-serif tracking-wider">
                    {time}
                  </div>
                </div>
              )}
            />
            
            {/* Date display */}
            <SimpleDateCard 
              dateTime={dateTime} 
              onDateChange={onDateChange}
              render={({ gregorian, hijri }) => (
                <div className="text-center space-y-1">
                  <div className="text-gray-600 text-base">
                    {gregorian}
                  </div>
                  <div 
                    className="text-sm font-semibold"
                    style={{ color: accentColorValue }}
                  >
                    {hijri}
                  </div>
                </div>
              )}
            />
          </div>
          
          {/* Decorative ornament */}
          <div 
            className="w-10 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent rounded-full"
            style={{ background: `linear-gradient(90deg, transparent, ${accentColorValue}, transparent)` }}
          />
          
          {/* Dua Section */}
          <SimpleDailyDuaCard 
            duas={duas} 
            currentDuaIndex={currentDuaIndex}
            onDuaChange={onDuaChange}
            render={({ arabic, translation }) => (
              <div className="max-w-md text-center space-y-4">
                <div 
                  className="text-lg font-semibold uppercase tracking-wider font-serif"
                  style={{ color: accentColorValue }}
                >
                  Daily Dua
                </div>
                <div className="text-lg leading-relaxed text-gray-800 text-right font-arabic">
                  {arabic || 'رَبَّنَا آتِنَا فِي الدُّنْيَا حَسَنَةً وَفِي الْآخِرَةِ حَسَنَةً وَقِنَا عَذَابَ النَّارِ'}
                </div>
                <div className="text-sm text-gray-600 italic leading-relaxed">
                  {translation || '"Our Lord, give us good in this world and good in the next world, and save us from the punishment of the Fire."'}
                </div>
              </div>
            )}
          />
        </div>
        
        {/* Right Section - Prayer Times and Countdown */}
        <div className="flex flex-col justify-center space-y-10">
          {/* Prayer Times Section */}
          <div className="bg-gray-50 rounded-xl p-8 border border-amber-100 shadow-lg">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold text-gray-800 font-serif">Prayer Times</h2>
              <div 
                className="w-16 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent mx-auto mt-2"
                style={{ background: `linear-gradient(90deg, transparent, ${accentColorValue}, transparent)` }}
              />
            </div>
            
            <SimplePrayerTimesCard 
              prayerTimes={prayerTimes}
              settings={settings}
              render={({ prayerTimes: times, prayers }) => (
                <div className="space-y-2">
                  {prayers && prayers.map(([prayer, time]) => (
                    <div 
                      key={prayer}
                      className="flex justify-between items-center p-4 mx-0 my-2 rounded-lg transition-all duration-300 border-l-4 border-transparent hover:bg-amber-50 hover:translate-x-1"
                    >
                      <span className="text-lg font-medium text-gray-800 font-serif capitalize">
                        {prayer}
                      </span>
                      <span className="text-lg font-semibold text-gray-600 font-serif">
                        {time}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            />
          </div>
          
          {/* Countdown Section */}
          <div className="bg-gray-50 rounded-xl p-8 text-center border border-amber-100 shadow-lg">
            <SimplePrayerCountdownCard 
              prayerTimes={prayerTimes}
              render={({ countdown, nextPrayer }) => (
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-800 font-serif">Next Prayer</h3>
                  <div 
                    className="text-4xl font-semibold tracking-wider font-serif"
                    style={{ color: accentColorValue }}
                  >
                    {countdown || '00:00:00'}
                  </div>
                  <div className="text-base text-gray-600 italic">
                    Until {nextPrayer || 'Next Prayer'}
                  </div>
                  
                  {/* Decorative ornament */}
                  <div 
                    className="w-10 h-1 bg-gradient-to-r from-transparent via-amber-500 to-transparent rounded-full mx-auto"
                    style={{ background: `linear-gradient(90deg, transparent, ${accentColorValue}, transparent)` }}
                  />
                  
                  {/* Location */}
                  <div className="flex items-center justify-center space-x-2 text-gray-800">
                    <svg className="w-5 h-5" style={{ color: accentColorValue }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <span className="font-medium">
                      {settings?.location ? settings.location.split(',')[0] : 'Location not set'}
                    </span>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassicElegantTemplate;